import React, { useEffect, useState } from "react";
import { Stack, Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useResponsive from "../../hooks/useResponsive";
import Header from "../../layout/dashboard/header";
import JobManagementContent from "../../components/jobManagement/JobBoardContent";
import { useMutation } from "@tanstack/react-query";
import { useNotificationService } from "../../components/notification";
import { apiGetJobs, apiGetJobStats } from "../../api/job";
import { JobListProps, JobStatsProps } from "../../interface/job_board";
import {
  PaginationDetails,
  PaginationDetailsDummyData,
} from "../../interface/api_request";
// import { apiGetFacilityJobs } from "../../api/jobs";

const JobManagement: React.FC = () => {
  const theme = useTheme();
  const { showNotification } = useNotificationService();
  const isMedium = useResponsive("down", "md", "xl");

  // set up pagination state
  const [pagination_state, setPagination_state] = useState<PaginationDetails>(
    PaginationDetailsDummyData,
  );
  const [filter_data, setFilter_data] = useState("");
  const [stats, setStats] = useState<JobStatsProps>({
    metrics: {
      employmentTypeDistribution: [
        {
          type: "",
          count: 0,
        },
      ],
      metrics: {
        totalApplicants: 0,
        totalPendingReviews: 0,
        totalShortlisted: 0,
        totalUpcomingInterviews: 0,
        rejectionRate: 0,
        acceptanceRate: 0,
        conversionRate: 0,
      },
    },
    totalDocuments: 0,
  });
  const [job_list, setJob_list] = useState<JobListProps[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // First call stats
        await statsRequest.mutateAsync();
        // Then call jobs
        await mutation.mutateAsync();
      } catch (error) {
        console.error("Error in sequential calls:", error);
      }
    };
    fetchData();
  }, [pagination_state.page, filter_data]);

  const mutation = useMutation({
    mutationFn: () => apiGetJobs(filter_data, `${pagination_state?.page}`),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        if (!!data?.data?.meta) {
          setPagination_state({
            hasNextPage: data?.data?.meta?.hasNextPage,
            hasPrevPage: data?.data?.meta?.hasPrevPage,
            nextPage: data?.data?.meta?.nextPage,
            page: data?.data?.meta?.page,
            pages: data?.data?.meta?.pages,
            prevPage: data?.data?.meta?.prevPage,
            reads: data?.data?.meta?.reads,
            totalDocs: data?.data?.meta?.totalDocs,
            unReads: data?.data?.meta?.unReads,
          });
        }
        if (data?.data?.data?.length > 0) {
          setJob_list(data.data.data);
        } else {
          setJob_list([]);
        }
      }
    },
    onError: (error) => {
      console.error("Fetch process failed", error);
      showNotification("Fetch process failed", "error");
    },
  });
  const statsRequest = useMutation({
    mutationFn: () => apiGetJobStats(),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        setStats(data?.data?.data[0]);
      }
    },
    onError: (error) => {
      showNotification("Stats fetch failed", "error");
    },
  });
  return (
    <>
      {/* Header Menu */}
      <Header>
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "row",
            gap: "10px",
            justifyContent: "space-between",
            alignItems: "center",
            marginRight: "1rem",
            height: isMedium ? 60 : 75,
          }}
        >
          <Stack direction={"row"} alignItems={"center"} sx={{ gap: "10px" }}>
            <Typography
              variant={"subtitle1"}
              sx={{
                fontWeight: 500,
                fontFamily: "Lexend",
                color: theme.palette.secondary.dark,
              }}
            >
              Job Management
            </Typography>
          </Stack>
        </Box>
      </Header>

      <JobManagementContent
        setFilterData={setFilter_data}
        jobList={job_list}
        isLoading={mutation.isPending}
        stats={stats}
        paginationState={pagination_state}
        setPaginationState={setPagination_state}
      />
    </>
  );
};

export default JobManagement;
